import { ReactNode } from 'react'
import { Editor as TiptapEditor } from '@tiptap/react'

import { cn } from '@/utils'
import {
  CheckboxIcon,
  ListBulletIcon,
  FontBoldIcon,
  FontItalicIcon,
  UnderlineIcon,
  TextAlignLeftIcon,
  TextAlignCenterIcon,
  TextAlignRightIcon,
  OpacityIcon,
  CheckIcon,
} from '@radix-ui/react-icons'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { OrderedListIcon } from '@/components/icons/OrderedListIcon'

import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarTrigger,
} from '@/components/ui/menubar'
import { ExtensionsEnum, HIGHLIGHT_COLORS, TEXT_COLORS } from './constants'
import { IconsMenu } from './IconsMenu'
import { ScrewdriverIcon } from '../icons/Screwdriver'

const Trigger = ({
  children,
  onClick,
  isActive,
}: {
  children: ReactNode
  onClick?: () => void
  isActive?: boolean
}) => {
  return (
    <MenubarTrigger
      className={cn('bg-white rounded-full cursor-pointer', {
        'bg-primary-45 text-white data-[state=open]:bg-primary-45 data-[state=open]:text-white':
          isActive,
        'hover:bg-white': !isActive,
      })}
      style={{ width: '30px', padding: '5px' }}
      onClick={() => {
        if (onClick) onClick()
      }}
    >
      {children}
    </MenubarTrigger>
  )
}

const ColorPicker = ({
  whiteCheck,
  colors,
  onColorSelect,
  isSelected,
}: {
  whiteCheck?: boolean
  colors: Array<{ value: string; name: string }>
  onColorSelect: (color: string) => void
  isSelected?: (color: string) => boolean
}) => (
  <>
    {colors.map((color) => (
      <MenubarItem
        key={color.value}
        className={cn('justify-start p-1')}
        onClick={() => onColorSelect(color.value)}
      >
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className="w-6 h-6 rounded-full flex items-center justify-center"
                style={{
                  backgroundColor: color.value,
                  border: '2px solid #d1d5db',
                }}
              >
                {isSelected?.(color.value) && (
                  <CheckIcon
                    className={cn('w-4 h-4', { 'text-white': whiteCheck })}
                  />
                )}
              </span>
            </TooltipTrigger>
            <TooltipContent className="bg-black">{color.name}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </MenubarItem>
    ))}
  </>
)

export const EditorMenuBar = ({
  editor,
  extensionsList,
  backgroundColor,
  updateBackgroundColorHandler,
}: {
  editor: TiptapEditor
  backgroundColor?: string
  extensionsList: Array<ExtensionsEnum>
  updateBackgroundColorHandler?: (color: string) => void
}) => {
  return (
    <Menubar className="bg-primary-20 border-primary-30 px-4 h-10">
      <MenubarMenu>
        <Trigger
          isActive={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <FontBoldIcon className="w-5 h-5" />
        </Trigger>
      </MenubarMenu>
      <MenubarMenu>
        <Trigger
          isActive={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <FontItalicIcon className="w-5 h-5" />
        </Trigger>
      </MenubarMenu>
      {extensionsList.includes(ExtensionsEnum.UNDERLINE) && (
        <MenubarMenu>
          <Trigger
            isActive={editor.isActive('underline')}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <UnderlineIcon className="w-5 h-5" />
          </Trigger>
        </MenubarMenu>
      )}
      {extensionsList.includes(ExtensionsEnum.TASK_LIST) && (
        <MenubarMenu>
          <Trigger>
            <ListBulletIcon className="w-5 h-5" />
          </Trigger>
          <MenubarContent className="min-w-[2rem]">
            <MenubarItem
              className={cn({
                'bg-primary-45 text-white': editor.isActive('bulletList'),
              })}
              onClick={() => {
                editor.chain().focus().toggleBulletList().run()
              }}
            >
              <ListBulletIcon className="w-5 h-5" />
            </MenubarItem>
            <MenubarItem
              className={cn({
                'bg-primary-45 text-white': editor.isActive('orderedList'),
              })}
              onClick={() => {
                editor.chain().focus().toggleOrderedList().run()
              }}
            >
              <OrderedListIcon className="w-4 h-4" />
            </MenubarItem>
          </MenubarContent>
        </MenubarMenu>
      )}
      {extensionsList.includes(ExtensionsEnum.TEXT_ALIGN) && (
        <MenubarMenu>
          <Trigger>
            <TextAlignLeftIcon className="w-5 h-5" />
            <MenubarContent className="min-w-[2rem]">
              <MenubarItem
                className={cn({
                  'bg-primary-45 text-white': editor.isActive({
                    textAlign: 'left',
                  }),
                })}
                onClick={() =>
                  editor.chain().focus().setTextAlign('left').run()
                }
              >
                <TextAlignLeftIcon className="w-5 h-5" />
              </MenubarItem>
              <MenubarItem
                className={cn({
                  'bg-primary-45 text-white': editor.isActive({
                    textAlign: 'center',
                  }),
                })}
                onClick={() =>
                  editor.chain().focus().setTextAlign('center').run()
                }
              >
                <TextAlignCenterIcon className="w-5 h-5" />
              </MenubarItem>
              <MenubarItem
                className={cn({
                  'bg-primary-45 text-white': editor.isActive({
                    textAlign: 'right',
                  }),
                })}
                onClick={() =>
                  editor.chain().focus().setTextAlign('right').run()
                }
              >
                <TextAlignRightIcon className="w-5 h-5" />
              </MenubarItem>
            </MenubarContent>
          </Trigger>
        </MenubarMenu>
      )}
      {extensionsList.includes(ExtensionsEnum.TASK_LIST) && (
        <MenubarMenu>
          <Trigger
            isActive={editor.isActive('taskList')}
            onClick={() => editor.chain().focus().toggleTaskList().run()}
          >
            <CheckboxIcon className="w-5 h-5" />
          </Trigger>
        </MenubarMenu>
      )}
      {(extensionsList.includes(ExtensionsEnum.COLOR) ||
        extensionsList.includes(ExtensionsEnum.HIGHLIGHT)) && (
        <MenubarMenu>
          <Trigger isActive={editor.isActive('textStyle')}>
            <div>
              <span className="w-5 h-5 flex items-center justify-center text-lg">
                A
              </span>
              <div
                className="absolute bottom-0 left-1 right-1 h-0.5"
                style={{
                  backgroundColor:
                    editor.getAttributes('textStyle')?.color || '#373530',
                }}
              />
            </div>
            <MenubarContent className="min-w-[2rem] grid grid-cols-5 gap-2 p-2">
              {extensionsList.includes(ExtensionsEnum.COLOR) && (
                <div className="col-span-5 text-xs font-medium py-1">
                  Text color
                </div>
              )}
              <ColorPicker
                whiteCheck={true}
                colors={TEXT_COLORS}
                onColorSelect={(color) =>
                  editor.chain().focus().toggleTextStyle({ color }).run()
                }
                isSelected={(color) =>
                  editor.getAttributes('textStyle')?.color === color
                }
              />
              {extensionsList.includes(ExtensionsEnum.HIGHLIGHT) && (
                <div className="col-span-5 text-xs font-medium py-1 mt-2">
                  Background color
                </div>
              )}
              <ColorPicker
                colors={HIGHLIGHT_COLORS}
                onColorSelect={(color) =>
                  editor.chain().focus().toggleHighlight({ color }).run()
                }
                isSelected={(color) =>
                  editor.getAttributes('highlight')?.color === color
                }
              />
            </MenubarContent>
          </Trigger>
        </MenubarMenu>
      )}
      {extensionsList.includes(ExtensionsEnum.ICONS) && (
        <MenubarMenu>
          <Trigger>
            <ScrewdriverIcon className="w-5 h-5" />
          </Trigger>
          <IconsMenu editor={editor} />
        </MenubarMenu>
      )}
      {extensionsList.includes(ExtensionsEnum.BACKGROUND) && (
        <MenubarMenu>
          <Trigger>
            <OpacityIcon className="w-5 h-5" />
          </Trigger>
          <MenubarContent className="min-w-[2rem] grid grid-cols-5 gap-2 p-2">
            <div className="col-span-5 text-xs font-medium py-1">
              Label background
            </div>
            <ColorPicker
              colors={HIGHLIGHT_COLORS}
              onColorSelect={(color) => updateBackgroundColorHandler?.(color)}
              isSelected={(color) => backgroundColor === color}
            />
          </MenubarContent>
        </MenubarMenu>
      )}
    </Menubar>
  )
}
